import { useQuery } from "@tanstack/react-query"
import { CornerDownLeft, Search, X } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useId, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router"

import { CONSULTA_MENU } from "@/componentes/layout/menu"
import { esCliente, esTransportista } from "@/lib/autorizacion"
import { cn } from "@/lib/cn"
import { usarSesion } from "@/lib/sesion"
import { buscar } from "./buscar"
import { type Destino, destinosDe } from "./destinos"
import { leerRecientes, recordarReciente } from "./recientes"

/**
 * El buscador de módulos y vistas (05/11, pedido del usuario): se escribe «pagos por
 * ejecutar», se pulsa y lleva a esa vista. Flota junto al botón de reportar para no
 * tapar ninguna pantalla y estar siempre a mano; también se abre con Ctrl + K.
 *
 * ⚠ **Sin autocompletado del navegador, y por eso NO es un formulario.** Lo que el
 * navegador recuerda y sugiere —correos, entradas anteriores— nace al ENVIAR un
 * `<form>`, y esto nunca se envía: Enter navega. Además el campo lleva
 * `autoComplete="off"`, no tiene `name` y lleva las marcas que respetan los gestores de
 * contraseñas (`data-1p-ignore`, `data-lpignore`, `data-bwignore`,
 * `data-form-type="other"`): sin ellas, uno de esos gestores ofrece correos en
 * cualquier campo de texto de una página con sesión.
 *
 * ⚠ **Sólo el personal interno**, como el botón de reportar: el importador y el
 * transportista tienen su propio menú corto.
 */
export function BuscadorModulos() {
  const usuario = usarSesion((e) => e.usuario)
  const ubicacion = useLocation()
  const navegar = useNavigate()
  const sinMovimiento = useReducedMotion()
  const interno = usuario !== null && !esCliente(usuario) && !esTransportista(usuario)
  const menu = useQuery({ ...CONSULTA_MENU, enabled: interno })
  const [abierto, setAbierto] = useState(false)
  const [texto, setTexto] = useState("")
  const [resaltado, setResaltado] = useState(0)
  const boton = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const idLista = useId()

  function cerrar(devolverFoco: boolean) {
    setAbierto(false)
    setTexto("")
    setResaltado(0)
    if (devolverFoco) boton.current?.focus()
  }

  // Ctrl + K (⌘ + K en Mac) abre y cierra desde cualquier pantalla.
  useEffect(() => {
    if (!interno) return
    function alAtajo(evento: KeyboardEvent) {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === "k") {
        evento.preventDefault()
        setAbierto((antes) => !antes)
        setTexto("")
        setResaltado(0)
      }
    }
    window.addEventListener("keydown", alAtajo)
    return () => window.removeEventListener("keydown", alAtajo)
  }, [interno])

  // Un toque fuera lo cierra; el propio botón no cuenta, que ya lo alterna.
  useEffect(() => {
    if (!abierto) return
    function fuera(evento: PointerEvent) {
      const donde = evento.target as Node
      if (panel.current?.contains(donde) || boton.current?.contains(donde)) return
      setAbierto(false)
      setTexto("")
      setResaltado(0)
    }
    document.addEventListener("pointerdown", fuera)
    return () => document.removeEventListener("pointerdown", fuera)
  }, [abierto])

  // ⚠ Escape en `window` y en CAPTURA, como `SelectorBuscable`: un `Modal` abierto
  // detrás escucha Escape en captura sobre `document` y se cerraría con éste.
  useEffect(() => {
    if (!abierto) return
    function alEscape(evento: KeyboardEvent) {
      if (evento.key !== "Escape") return
      evento.stopPropagation()
      setAbierto(false)
      setTexto("")
      setResaltado(0)
      boton.current?.focus()
    }
    window.addEventListener("keydown", alEscape, true)
    return () => window.removeEventListener("keydown", alEscape, true)
  }, [abierto])

  if (usuario === null || !interno || ubicacion.pathname.startsWith("/portal")) return null

  // Fijado aquí: dentro de `ir`, que es una declaración, TypeScript no ve el `null` de arriba.
  const usuarioId = usuario.id
  const destinos = destinosDe(menu.data ?? [], usuario)
  const vacio = texto.trim() === ""
  // Con el campo vacío, lo último que se abrió desde aquí y que todavía se puede abrir.
  const filas = vacio
    ? leerRecientes(usuarioId)
        .map((ruta) => destinos.find((d) => d.ruta === ruta))
        .filter((d): d is Destino => d !== undefined)
    : buscar(destinos, texto)

  function ir(destino: Destino | undefined) {
    if (destino === undefined) return
    recordarReciente(usuarioId, destino.ruta)
    cerrar(false)
    navegar(destino.ruta)
  }

  function alTeclear(evento: React.KeyboardEvent<HTMLInputElement>) {
    if (evento.key === "ArrowDown") {
      evento.preventDefault()
      setResaltado((r) => (filas.length === 0 ? 0 : (r + 1) % filas.length))
    } else if (evento.key === "ArrowUp") {
      evento.preventDefault()
      setResaltado((r) => (filas.length === 0 ? 0 : (r - 1 + filas.length) % filas.length))
    } else if (evento.key === "Enter") {
      evento.preventDefault()
      ir(filas[resaltado])
    }
  }

  const idFila = (i: number) => `${idLista}-${i}`

  return (
    <>
      <motion.button
        ref={boton}
        type="button"
        onClick={() => (abierto ? cerrar(false) : setAbierto(true))}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        data-testid="abrir-buscador"
        title="Buscar un módulo o una vista (Ctrl + K)"
        className="no-print flota-abajo-2 fixed z-40 flex h-12 w-12 items-center justify-center rounded-full border shadow-lg"
        style={{
          backgroundColor: "var(--surface-elevated)",
          borderColor: "var(--border)",
          color: "var(--fg)",
        }}
        initial={sinMovimiento ? false : { opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={sinMovimiento ? undefined : { scale: 1.06 }}
        whileTap={sinMovimiento ? undefined : { scale: 0.94 }}
        transition={
          sinMovimiento ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 30 }
        }
      >
        <Search size={20} aria-hidden={true} />
        <span className="sr-only">Buscar un módulo o una vista</span>
      </motion.button>

      <AnimatePresence>
        {abierto && (
          <motion.div
            ref={panel}
            role="dialog"
            aria-label="Buscar un módulo o una vista"
            // En el teléfono, arriba: abajo lo taparía el teclado. En pantalla ancha,
            // encima de los dos botones, con alto fijo para que el campo no salte
            // mientras cambian los resultados.
            className="panel-flotante no-print fixed inset-x-3 top-[calc(0.75rem+env(safe-area-inset-top))] z-50 flex max-h-[70vh] flex-col gap-2 p-3 sm:inset-x-auto sm:top-auto sm:right-[calc(1.25rem+env(safe-area-inset-right))] sm:bottom-[calc(5rem+env(safe-area-inset-bottom))] sm:h-[26rem] sm:max-h-[calc(100vh-7rem)] sm:w-[24rem]"
            initial={sinMovimiento ? false : { opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={sinMovimiento ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: sinMovimiento ? 0 : 0.16, ease: "easeOut" }}
          >
            <div className="flex items-center gap-2">
              <input
                // biome-ignore lint/a11y/noAutofocus: se abre para escribir; el foco va donde se escribe
                autoFocus={true}
                type="text"
                role="combobox"
                aria-label="Buscar un módulo o una vista"
                aria-expanded={filas.length > 0}
                aria-controls={idLista}
                aria-autocomplete="list"
                aria-activedescendant={filas.length > 0 ? idFila(resaltado) : undefined}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="go"
                data-1p-ignore=""
                data-lpignore="true"
                data-bwignore=""
                data-form-type="other"
                placeholder="Pagos por ejecutar, nómina, bancos…"
                value={texto}
                onChange={(e) => {
                  setTexto(e.target.value)
                  setResaltado(0)
                }}
                onKeyDown={alTeclear}
                className="campo min-w-0 flex-1"
              />
              <button
                type="button"
                onClick={() => cerrar(true)}
                aria-label="Cerrar el buscador"
                className="foco-cristal inline-flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-control)]"
              >
                <X size={16} aria-hidden={true} />
              </button>
            </div>

            {vacio && filas.length > 0 && (
              <p className="px-1 text-xs font-medium" style={{ color: "var(--fg-muted)" }}>
                Abiertos hace poco
              </p>
            )}
            <div id={idLista} role="listbox" className="min-h-0 flex-1 overflow-y-auto text-sm">
              {filas.map((destino, i) => (
                <div
                  key={destino.ruta}
                  id={idFila(i)}
                  role="option"
                  aria-selected={i === resaltado}
                  tabIndex={-1}
                  // `mousedown` para que el campo no pierda el foco antes de ir.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    ir(destino)
                  }}
                  onMouseEnter={() => setResaltado(i)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] px-2.5 py-2",
                    i === resaltado && "activo-cristal",
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{destino.etiqueta}</span>
                    <span className="block truncate text-xs" style={{ color: "var(--fg-muted)" }}>
                      {destino.donde}
                    </span>
                  </span>
                  {i === resaltado && (
                    <CornerDownLeft
                      size={14}
                      aria-hidden={true}
                      style={{ color: "var(--fg-muted)" }}
                    />
                  )}
                </div>
              ))}
              {!vacio && filas.length === 0 && (
                <p className="px-2.5 py-2" style={{ color: "var(--fg-muted)" }}>
                  {menu.isPending
                    ? "Cargando los módulos…"
                    : `Nada se llama «${texto.trim()}». Prueba con otra palabra.`}
                </p>
              )}
              {vacio && filas.length === 0 && (
                <p className="px-2.5 py-2" style={{ color: "var(--fg-muted)" }}>
                  Escribe el nombre de un módulo o de una vista: «pagos por ejecutar», «nómina»,
                  «tasas»…
                </p>
              )}
            </div>
            <p
              className="hidden px-1 text-xs sm:block"
              style={{ color: "var(--fg-muted)" }}
              aria-hidden={true}
            >
              ↑ ↓ para elegir · Intro para abrir · Esc para cerrar
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
